import { ArchiveFileType } from "@/lib/supabase/supabaseTypes";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { useCentralStore } from "../../../store/Central";
import { Button } from "../../../components/ui/button";
import { ArchiveFileRow } from "./archive-file-row.component";
import { ArchiveFileTableHeader } from "./archive-file-table-header.component";

interface ArchiveFlatFileListProps {
	files: ArchiveFileType[];
	hasMore: boolean;
	loadingMore: boolean;
	selectedIds: Set<string>;
	showClient: boolean;
	onToggleFile: (fileId: string) => void;
	onPreviewFile: (file: ArchiveFileType, siblingIds: string[]) => void;
	onDownloadFile: (file: ArchiveFileType) => void;
	onOpenJob: (file: ArchiveFileType) => void;
	onLoadMore: () => void;
}

export const ArchiveFlatFileList: React.FC<ArchiveFlatFileListProps> = ({
	files,
	hasMore,
	loadingMore,
	selectedIds,
	showClient,
	onToggleFile,
	onPreviewFile,
	onDownloadFile,
	onOpenJob,
	onLoadMore,
}) => {
	const clientsLookup = useCentralStore((state) => state.clientsLookup);

	const getClientLabel = (file: ArchiveFileType) => {
		const client = file.resolved_client_id
			? clientsLookup[file.resolved_client_id]
			: undefined;
		return client ? getClientDisplayName(client) : "Ohne Auftraggeber";
	};

	const loadedIds = (files.map((f) => f.id).filter(Boolean) ??
		[]) as string[];

	return (
		<div>
			<div className="rounded-md border border-gray-200">
				<ArchiveFileTableHeader
					showClient={showClient}
					showJobContext
				/>
				<div className="divide-y divide-gray-100">
					{files.map((file) => (
						<ArchiveFileRow
							key={file.id}
							file={file}
							selected={selectedIds.has(file.id ?? "")}
							showJobContext
							clientLabel={
								showClient ? getClientLabel(file) : undefined
							}
							onToggleSelect={onToggleFile}
							onPreview={(f) => onPreviewFile(f, loadedIds)}
							onDownload={onDownloadFile}
							onOpenJob={onOpenJob}
						/>
					))}
				</div>
			</div>
			{hasMore && (
				<div className="flex justify-center py-4">
					<Button
						variant="outline"
						onClick={onLoadMore}
						disabled={loadingMore}
						aria-label="Weitere Dateien laden"
						data-testid="archive-flat-load-more"
					>
						{loadingMore
							? "Wird geladen…"
							: "Weitere Dateien laden"}
					</Button>
				</div>
			)}
		</div>
	);
};
